import { decideEdd, fetchEddReview } from "api/admin/kycReviews";
import useAdminAccess from "hooks/useAdminAccess";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Applicant,
  AuditHistory,
  dateLabel,
  DecisionButtons,
  DecisionForm,
  humanize,
  reviewKey,
  ReviewError,
  ReviewFields,
  ReviewLayout,
  ReviewLoading,
  ReviewSection,
} from "./ReviewComponents";

const volumeLabels: Record<string, string> = {
  below_5m: "Below ₦5,000,000",
  from_5m_to_10m: "₦5,000,000 – ₦10,000,000",
  from_10m_to_25m: "₦10,000,000 – ₦25,000,000",
  above_25m: "Above ₦25,000,000",
};
const frequencyLabels: Record<string, string> = {
  one_time: "One-time",
  occasional: "Occasional (1–3 transactions/month)",
  regular: "Regular (4–10 transactions/month)",
  frequent: "Frequent (11–30 transactions/month)",
  very_frequent: "Very frequent (30+ transactions/month)",
};

export default function EddReview() {
  const { userID = "" } = useParams();
  return <EddReviewForUser key={userID} userID={userID} />;
}

function EddReviewForUser({ userID }: { userID: string }) {
  const { admin, can } = useAdminAccess();
  const client = useQueryClient();
  const key = [...reviewKey("edd", admin?.id), "detail", userID];
  const query = useQuery(key, ({ signal }) => fetchEddReview(userID, signal), {
    enabled: can("users.kyc.view") && /^\d+$/.test(userID),
    retry: false,
  });
  const [decision, setDecision] = useState<{
    action: "approve" | "reject";
    submissionID: number;
  } | null>(null);
  const mutation = useMutation(
    ({
      action,
      reason,
      reapplicationAt,
    }: {
      action: "approve" | "reject";
      reason: string;
      reapplicationAt?: string;
    }) =>
      decideEdd(
        userID,
        action === "approve" ? "approve" : "deny",
        reason,
        reapplicationAt,
      ),
    {
      onSuccess: (detail) => {
        client.setQueryData(key, detail);
        setDecision(null);
        toast.success("Tier 3 review saved.");
        client.invalidateQueries(["kycReviews", "edd"]);
      },
      onError: () => {
        query.refetch();
      },
    },
  );
  const data = query.data;
  const canDecide =
    can("users.kyc.approve") && data?.reviewStatus === "submitted";
  const busy = mutation.isLoading || query.isFetching;
  return (
    <ReviewLayout kind="edd" detail>
      {query.isLoading ? (
        <ReviewLoading />
      ) : query.isError ? (
        <ReviewError error={query.error} retry={() => query.refetch()} />
      ) : data ? (
        <>
          <Applicant user={data.user} userID={data.userID} />
          <ReviewSection
            title={`Latest submission · Attempt ${data.attemptNumber}`}
            status={data.reviewStatus}
          >
            <ReviewFields
              fields={[
                ["Occupation", data.occupation],
                ["Source of funds", humanize(data.sourceOfFunds)],
                ["Source of wealth", humanize(data.sourceOfWealth)],
                [
                  "Expected transaction volume",
                  volumeLabels[data.expectedTransactionVolume] ??
                    humanize(data.expectedTransactionVolume),
                ],
                [
                  "Expected transaction frequency",
                  frequencyLabels[data.expectedTransactionFrequency] ??
                    humanize(data.expectedTransactionFrequency),
                ],
                [
                  "Nature of trading activity",
                  humanize(data.natureOfTradingActivity),
                ],
                ["Submitted", dateLabel(data.submittedAt)],
                ["Reviewed", dateLabel(data.reviewedAt)],
                ...(data.declinedReason
                  ? [
                      ["Rejection reason", data.declinedReason] as [
                        string,
                        string,
                      ],
                    ]
                  : []),
                ...(data.reapplicationAvailableAt
                  ? [
                      [
                        "Reapplication available from",
                        dateLabel(data.reapplicationAvailableAt),
                      ] as [string, string],
                    ]
                  : []),
              ]}
            />
            {canDecide && !decision && (
              <DecisionButtons
                title="Tier 3"
                disabled={busy}
                choose={(action) => {
                  mutation.reset();
                  setDecision({ action, submissionID: data.id });
                }}
              />
            )}
            {decision && canDecide && (
              <DecisionForm
                title={
                  decision.action === "approve"
                    ? "Approve Tier 3"
                    : "Reject Tier 3"
                }
                description={
                  decision.action === "approve"
                    ? "This approves the customer's EDD submission and upgrades their account to Tier 3."
                    : "Explain what the customer must correct before submitting again."
                }
                rejecting={decision.action === "reject"}
                allowReapplication
                pending={mutation.isLoading}
                disabled={busy || decision.submissionID !== data.id}
                error={mutation.error}
                onCancel={() => setDecision(null)}
                onSubmit={(reason, reapplicationAt) =>
                  mutation.mutateAsync({
                    action: decision.action,
                    reason,
                    reapplicationAt,
                  })
                }
              />
            )}
            {decision && decision.submissionID !== data.id && (
              <p role="alert" className="mt-3 text-sm text-red-700">
                The submission changed. Cancel this decision and review the
                latest answers.
              </p>
            )}
          </ReviewSection>
          <AuditHistory logs={data.auditLogs} />
        </>
      ) : (
        <p role="alert">No Tier 3 submission is available for this user.</p>
      )}
    </ReviewLayout>
  );
}
